import { useEffect, useState } from "react";
import emailjs from "@emailjs/browser";
import Swal from "sweetalert2";
import Select from "react-select";
import TextField from "@mui/material/TextField";
import { FaArrowCircleRight } from "react-icons/fa";
import { useVehicles } from "../context/VehiclesContext";
import { validateEmail } from "../utils/validation";

function RequestQuoteForm({ idPrefix = "" }) {
  const { vehicles, loading, error } = useVehicles();

  const [carType, setCarType] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAIL_PUBLIC_KEY);
  }, []);

  const fieldId = (name) => (idPrefix ? `${idPrefix}-${name}` : name);

  const validateForm = () => {
    if (!carType) {
      Swal.fire(" Select Your Car", "Please select a car type", "error");
      return false;
    }
    if (!name) {
      Swal.fire("Full Name", "Name is required", "error");
      return false;
    }
    if (!phone) {
      Swal.fire("Phone Number", "Phone number is required", "error");
      return false;
    }
    if (!email || !validateEmail(email)) {
      Swal.fire("Email", "Please enter a valid email", "error");
      return false;
    }
    if (!message) {
      Swal.fire("Message", "Message is required", "error");
      return false;
    }
    return true;
  };

  const confirmBooking = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      carType: carType.label,
      name,
      phone,
      email,
      message,
      to_email: "info@ezcarrentals.com.au",
      form_source: "Request a Quote Form",
      enquiry_type: "Request a Quote Form",
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAIL_SERVICE_ID,
        process.env.REACT_APP_EMAIL_TEMPLATE_ID,
        payload,
        process.env.REACT_APP_EMAIL_PUBLIC_KEY
      );

      Swal.fire({
        icon: "success",
        title: "Request Sent",
        text: "Thank you for enquiring. Our friendly staff will get back to you shortly!",
      });

      setCarType("");
      setName("");
      setPhone("");
      setEmail("");
      setMessage("");
    } catch (sendError) {
      console.error("Error sending email:", sendError);
      Swal.fire({
        icon: "error",
        title: "Failed to Send Request",
        text: "Something went wrong. Please try again or contact us on 0400 920 512.",
      });
    }
  };

  const carList = vehicles.map((data) => ({
    value: data.id,
    label: data.vehicleName,
  }));

  return (
    <div className='book-content-bg'>
      <h2 className='book-car-title'>
        <span>Request a Quote</span>
      </h2>
      {error && (
        <p role='alert' style={{ color: "#ffb4b4", marginBottom: "12px" }}>
          {error}
        </p>
      )}
      <form className='box-form' onSubmit={confirmBooking}>
        <div className='box-form__car-type'>
          <label htmlFor={fieldId("carType")}>
            <i className='fa-solid fa-car'></i> &nbsp; Select Your Car
          </label>
          <Select
            inputId={fieldId("carType")}
            value={carType}
            onChange={setCarType}
            options={carList}
            placeholder={
              loading ? "Loading vehicles..." : "Select your car type"
            }
            isDisabled={loading || Boolean(error)}
            isSearchable={true}
            classNamePrefix='custom-select'
            className='custom-select-container'
            styles={{
              menu: (provided) => ({
                ...provided,
                maxHeight: "300px",
                overflowY: "scroll",
                zIndex: 1000,
              }),
            }}
          />
        </div>
        <div className='box-form__car-time'>
          <label htmlFor={fieldId("name")}>
            <i className='fa-regular fa-calendar-days '></i> &nbsp;Full Name
          </label>
          <TextField
            placeholder='Enter your name'
            variant='outlined'
            margin='normal'
            fullWidth
            id={fieldId("name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ m: 0, cursor: "auto" }}
          />
        </div>
        <div className='box-form__car-time'>
          <label htmlFor={fieldId("phone")}>
            <i className='fa-regular fa-calendar-days '></i> &nbsp;Phone
          </label>
          <TextField
            placeholder='+61 xxxx xxx xxx'
            variant='outlined'
            margin='normal'
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type='text'
            fullWidth
            id={fieldId("phone")}
            sx={{ m: 0 }}
          />
        </div>
        <div className='box-form__car-time'>
          <label htmlFor={fieldId("email")}>
            <i className='fa-regular fa-calendar-days ' aria-hidden='true'></i>{" "}
            &nbsp;Email
          </label>
          <TextField
            placeholder='Email'
            variant='outlined'
            margin='normal'
            fullWidth
            id={fieldId("email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type='email'
            error={Boolean(email) && !validateEmail(email)}
            helperText={
              Boolean(email) && !validateEmail(email) ? "Invalid email" : ""
            }
            sx={{ m: 0 }}
          />
        </div>
        <div className='box-form__car-time'>
          <label htmlFor={fieldId("message")}>
            <i className='fa-regular fa-calendar-days'></i> &nbsp;Message
          </label>
          <TextField
            placeholder='Message'
            variant='outlined'
            name='message'
            margin='normal'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            id={fieldId("message")}
            multiline
            rows={3}
            sx={{
              m: 0,
              borderRadius: "5px",
              backgroundColor: "transparent",
              color: "#fff",
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ffffffba",
                },
                "&:hover fieldset": {
                  borderColor: "#0071ff",
                },
                "& input, & textarea": {
                  color: "#ffffff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#0071ff",
                },
              },
            }}
          />
        </div>
        <div className='box-form__car-time'>
          <label>
            <i className='fa-regular fa-calendar-days '></i> &nbsp;
          </label>
          <div className='search-btn'>
            <button type='submit'>
            Click here to Send &nbsp; <FaArrowCircleRight />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default RequestQuoteForm;
